/**
 * De eigen pagina van de deelnemer: gegevens aanvullen, toestemming geven, kiezen.
 *
 * Dit scherm is de spil van de uitnodigingsstroom. Zolang mailen uitstaat is de link
 * hiernaartoe (de vierde link op het beheerscherm) de énige manier om iemand aan zijn
 * vragenlijst te helpen.
 *
 * Wat er wel en niet te wijzigen valt:
 *   — naam en geboortedatum staan er ter controle, niet te wijzigen;
 *   — het e-mailadres staat er ook alleen ter controle. Wie deze link per ongeluk in
 *     handen krijgt, zou het adres anders op zichzelf kunnen zetten en daarna de
 *     vragenlijst-link naar zichzelf laten sturen. Klopt het adres niet (of ontbreekt
 *     het), dan staat er een contactregel — die verwijst naar een beheerder;
 *   — telefoon, adres en de toestemming vult de deelnemer zelf in.
 *
 * De pagina rekent níet zelf uit of de gegevens compleet zijn. Dat zegt de server
 * (`compleet` en `ontbrekende_velden`), en `bepaalGegevensWeergave` bepaalt welke tak er
 * te zien is. Een tweede versie van die regel hier zou gegarandeerd uit de pas gaan lopen
 * met de servergrens — en dan botst de deelnemer op een foutmelding op het moment dat hij
 * denkt klaar te zijn.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ErrorBanner, PageShell } from "../components/PageShell";
import { Button, Input } from "../components/fulkra";
import { api, formatApiError, withRetry } from "../lib/api";
import { SUPPORT_EMAIL } from "../lib/contact";
import { ontbrekendeVeldenZin } from "../lib/namen";
import { laterKeuzeUitleg, nulmetingLinkMelding, verzendenStaatAan } from "../lib/verzendstand";
import {
  bepaalGegevensWeergave, AAN_HET_LADEN, INCOMPLEET, KEUZE, AL_INGEVULD,
} from "../lib/gegevensWeergave";

/** Wat de deelnemer op dit scherm mag invullen — precies wat de PATCH accepteert. */
const INVULVELDEN = ["telefoon", "straat", "huisnummer", "huisnummer_toevoeging", "postcode", "plaats"];

/** Dezelfde controle als het aanmeldformulier en de server (backend: POSTCODE_PATROON). */
function postcodeGeldig(v) { return /^\d{4}\s?[A-Za-z]{2}$/.test(v); }

function formatDatum(waarde) {
  if (!waarde) return "—";
  const d = new Date(waarde);
  if (Number.isNaN(d.getTime())) return String(waarde);
  return d.toLocaleDateString("nl-NL", { day: "2-digit", month: "2-digit", year: "numeric" });
}

const KOP = { font: "var(--text-h2)", letterSpacing: "var(--ls-kop)", color: "var(--text-strong)" };
const LEES_REGEL = { fontSize: "var(--fs-body)", color: "var(--text-body)" };

export default function GegevensPage() {
  const { progressie_token } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [form, setForm] = useState(null);
  const [toestemming, setToestemming] = useState(false);
  const [veldFouten, setVeldFouten] = useState({});
  const [fout, setFout] = useState(null);
  const [laterGemeld, setLaterGemeld] = useState(null);

  const q = useQuery({
    queryKey: ["gegevens", progressie_token],
    queryFn: async () =>
      (await withRetry(() => api.get(`/werknemer/${progressie_token}/gegevens`))).data,
  });

  // Het formulier één keer vullen met wat er al staat. Daarna is de invoer van de
  // deelnemer leidend: zou dit bij elke verversing opnieuw draaien, dan zag hij zijn
  // half getypte adres onder zijn handen terugspringen.
  useEffect(() => {
    if (!q.data || form !== null) return;
    const start = {};
    for (const veld of INVULVELDEN) start[veld] = q.data[veld] || "";
    setForm(start);
    setToestemming(Boolean(q.data.avg_toestemming));
  }, [q.data, form]);

  // Vertrekt er op dit moment echt mail? Zonder deze vraag beloofde dit scherm dat de
  // vragenlijst-link naar het e-mailadres ging, terwijl `EMAIL_SENDER_ENABLED` uit staat
  // en het bericht op `pending` blijft staan. Dan wacht iemand op een mail die niet komt.
  // Mislukt deze aanvraag, dan is de stand onbekend en wordt er niets beweerd.
  const verzendQ = useQuery({
    queryKey: ["api-root", "verzendstand"],
    queryFn: async () => (await api.get("/")).data,
    staleTime: 60 * 60 * 1000,
    retry: false,
  });
  const verzendenAan = verzendenStaatAan(verzendQ.data);

  const gegevens = q.data;
  // De keuze uit één bron: dezelfde functie die `BaselinePage` gebruikt om te bepalen
  // of hij moet doorsturen. Zolang het antwoord onderweg is, wordt er niets beslist.
  const weergave = q.isLoading || q.error ? AAN_HET_LADEN : bepaalGegevensWeergave(gegevens);

  const opslaan = useMutation({
    mutationFn: (lichaam) => api.patch(`/werknemer/${progressie_token}/gegevens`, lichaam),
    onSuccess: (res) => {
      setFout(null);
      qc.setQueryData(["gegevens", progressie_token], res.data);
    },
    onError: (e) => setFout(formatApiError(e)),
  });

  const later = useMutation({
    mutationFn: () => api.post(`/werknemer/${progressie_token}/nulmeting-later`),
    onSuccess: (res) => {
      setFout(null);
      setLaterGemeld(res.data);
    },
    onError: (e) => setFout(formatApiError(e)),
  });

  function zet(veld, waarde) {
    setForm((f) => ({ ...f, [veld]: waarde }));
    setVeldFouten((cur) => {
      const volgend = { ...cur };
      delete volgend[veld];
      return volgend;
    });
  }

  function versturen() {
    const fouten = {};
    if (form.postcode && !postcodeGeldig(form.postcode)) {
      fouten.postcode = "Postcode in de vorm 1234 AB";
    }
    setVeldFouten(fouten);
    if (Object.keys(fouten).length > 0) return;

    const lichaam = {};
    for (const veld of INVULVELDEN) {
      const waarde = String(form[veld] ?? "").trim();
      if (waarde) lichaam[veld] = waarde;
    }
    // Alleen meesturen als er nog geen toestemming was; `false` weigert de server
    // bewust — intrekken loopt via een beheerder, niet via dit vinkje.
    if (toestemming && !gegevens.avg_toestemming) lichaam.avg_toestemming = true;
    opslaan.mutate(lichaam);
  }

  // De fout eerst: bij een mislukte aanvraag blijft `form` leeg, en dan zou de
  // laad-melding hieronder eeuwig blijven staan in plaats van te vertellen wat er is.
  if (q.error) {
    return (
      <PageShell title="Uw gegevens">
        <p data-testid="gegevens-fout" style={{ ...LEES_REGEL, color: "var(--text-alarm)" }}>
          Deze link is niet (meer) geldig. Neem contact op via{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: "var(--text-action)" }}>{SUPPORT_EMAIL}</a>.
        </p>
      </PageShell>
    );
  }

  if (q.isLoading || form === null) {
    return (
      <PageShell title="Uw gegevens">
        <p data-testid="gegevens-laden" style={LEES_REGEL}>Uw gegevens worden opgehaald…</p>
      </PageShell>
    );
  }

  const naam = [gegevens.voorletters, gegevens.achternaam].filter(Boolean).join(" ");

  return (
    <PageShell title="Uw gegevens">
      <div data-testid="gegevens-page" style={{ display: "grid", gap: "var(--ruimte-7)" }}>
        <header>
          <h1 style={KOP}>Uw gegevens</h1>
          <p style={{ marginTop: "var(--ruimte-4)", ...LEES_REGEL, color: "var(--text-soft)" }}>
            {weergave === AL_INGEVULD
              ? "Hieronder staat wat er van u bekend is."
              : "Controleer wat er van u bekend is en vul aan wat er nog ontbreekt. "
                + "Daarna kunt u de vragenlijst invullen."}
          </p>
        </header>

        <ErrorBanner error={fout} />

        {/* Wat vaststaat: naam, geboortedatum en het e-mailadres. */}
        <section
          data-testid="gegevens-vast"
          style={{
            display: "grid", gap: "var(--ruimte-4)", borderRadius: "var(--radius)",
            border: "1px solid var(--border-subtle)", background: "var(--wit)",
            padding: "var(--ruimte-6)",
          }}
        >
          <div style={LEES_REGEL}><strong>Naam</strong><br />{naam || "—"}</div>
          <div style={LEES_REGEL}>
            <strong>Geboortedatum</strong><br />{formatDatum(gegevens.geboortedatum)}
          </div>
          <div style={LEES_REGEL}>
            <strong>E-mailadres</strong><br />
            <span data-testid="gegevens-email">{gegevens.email || "nog niet bekend"}</span>
            {/* Deze regel staat er altijd, ook (júist) als het adres ontbreekt: een
                deelnemer zonder e-mailadres kan zichzelf langs deze weg niet compleet
                maken, en zou zonder deze verwijzing doodlopen. */}
            <br />
            <span data-testid="gegevens-contactregel" style={{ fontSize: "var(--fs-sm)", color: "var(--text-soft)" }}>
              {gegevens.email ? "Klopt dit adres niet?" : "Nog geen e-mailadres bekend?"} Neem contact op via{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: "var(--text-action)" }}>{SUPPORT_EMAIL}</a>.
            </span>
          </div>
        </section>

        {weergave === AL_INGEVULD ? (
          <p
            data-testid="gegevens-al-ingevuld"
            style={{
              ...LEES_REGEL, borderRadius: "var(--radius)", border: "1px solid var(--border-subtle)",
              background: "var(--wit)", padding: "var(--ruimte-6)",
            }}
          >
            U heeft de vragenlijst al ingevuld. U hoeft nu niets meer te doen — wij nemen
            contact met u op voor de volgende stap.
          </p>
        ) : (
          <>
            {weergave === INCOMPLEET && gegevens.ontbrekende_velden?.length > 0 ? (
              <p data-testid="gegevens-ontbreekt" style={{ ...LEES_REGEL, color: "var(--text-soft)" }}>
                Er ontbreekt nog: {ontbrekendeVeldenZin(gegevens.ontbrekende_velden)}.
              </p>
            ) : null}

            {weergave === KEUZE ? (
              <p data-testid="gegevens-compleet-melding" style={{ ...LEES_REGEL, color: "var(--text-soft)" }}>
                Uw gegevens zijn compleet.
              </p>
            ) : null}

            <div style={{ display: "grid", gap: "var(--ruimte-6)" }}>
              <Veld naam="telefoon" label="Telefoonnummer" type="tel" placeholder="06-12345678"
                waarde={form.telefoon} fout={veldFouten.telefoon} onWijzig={zet} />
              <Veld naam="straat" label="Straat" waarde={form.straat}
                fout={veldFouten.straat} onWijzig={zet} />
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "var(--ruimte-5)" }}>
                <Veld naam="huisnummer" label="Huisnummer" waarde={form.huisnummer}
                  fout={veldFouten.huisnummer} onWijzig={zet} />
                <Veld naam="huisnummer_toevoeging" label="Toevoeging" placeholder="bv. A"
                  waarde={form.huisnummer_toevoeging} fout={veldFouten.huisnummer_toevoeging} onWijzig={zet} />
              </div>
              <Veld naam="postcode" label="Postcode" placeholder="1234 AB"
                waarde={form.postcode} fout={veldFouten.postcode} onWijzig={zet} />
              <Veld naam="plaats" label="Plaats" waarde={form.plaats}
                fout={veldFouten.plaats} onWijzig={zet} />
            </div>

            {/* De toestemming. Staat hij er al, dan valt er niets meer aan te vinken —
                ook niet als een beheerder hem namens de deelnemer vastlegde. */}
            {gegevens.avg_toestemming ? (
              <p data-testid="avg-al-gegeven" style={{ ...LEES_REGEL, color: "var(--text-soft)" }}>
                Uw toestemming voor het verwerken van uw gegevens is geregistreerd.
              </p>
            ) : (
              <label
                data-testid="avg-gegevens-label"
                style={{ display: "flex", alignItems: "flex-start", gap: "var(--ruimte-5)", minHeight: "var(--touch-min)", cursor: "pointer" }}
              >
                <input
                  type="checkbox"
                  data-testid="avg-gegevens-checkbox"
                  checked={toestemming}
                  onChange={(e) => setToestemming(e.target.checked)}
                  style={{ marginTop: 2, marginLeft: 4, width: 20, height: 20, accentColor: "var(--actie)", flexShrink: 0 }}
                />
                <span style={LEES_REGEL}>
                  Ik ga akkoord met de{" "}
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "var(--text-action)", textDecoration: "underline", textUnderlineOffset: 2 }}>
                    privacy-voorwaarden
                  </Link>
                  {" "}en geef toestemming voor verwerking van mijn gegevens.
                </span>
              </label>
            )}

            <Button size="touch" onClick={versturen} disabled={opslaan.isPending} data-testid="btn-gegevens-opslaan">
              {opslaan.isPending ? "Bezig met opslaan…" : "Gegevens opslaan"}
            </Button>

            {weergave === KEUZE ? (
              <section
                data-testid="gegevens-keuze"
                style={{
                  display: "grid", gap: "var(--ruimte-5)", borderRadius: "var(--radius)",
                  border: "1px solid var(--border-subtle)", background: "var(--wit)",
                  padding: "var(--ruimte-6)",
                }}
              >
                <h2 style={{ font: "var(--text-h3)", color: "var(--text-strong)" }}>De vragenlijst</h2>
                {laterGemeld ? (
                  <p data-testid="later-bevestiging" style={LEES_REGEL}>
                    {nulmetingLinkMelding(Boolean(laterGemeld.al_verstuurd), verzendenAan)}
                  </p>
                ) : (
                  <p data-testid="later-uitleg" style={{ ...LEES_REGEL, color: "var(--text-soft)" }}>
                    {laterKeuzeUitleg(verzendenAan)}
                  </p>
                )}
                <div style={{ display: "grid", gap: "var(--ruimte-5)" }}>
                  <Button
                    size="touch"
                    onClick={() => navigate(`/baseline/${progressie_token}`)}
                    data-testid="btn-nu-invullen"
                  >
                    Nu invullen
                  </Button>
                  <Button
                    size="touch"
                    variant="secundair"
                    onClick={() => later.mutate()}
                    disabled={later.isPending || Boolean(laterGemeld)}
                    data-testid="btn-liever-later"
                  >
                    {later.isPending ? "Bezig…" : "Liever later"}
                  </Button>
                </div>
              </section>
            ) : null}
          </>
        )}
      </div>
    </PageShell>
  );
}

function Veld({ naam, label, type = "text", placeholder = "", waarde, fout, onWijzig }) {
  return (
    <div style={{ display: "grid", gap: "var(--ruimte-3)" }}>
      <label htmlFor={`gegevens-${naam}`} style={{ font: "var(--text-label)", color: "var(--text-strong)" }}>
        {label}
      </label>
      <Input
        id={`gegevens-${naam}`}
        type={type}
        value={waarde ?? ""}
        onChange={(e) => onWijzig(naam, e.target.value)}
        placeholder={placeholder}
        data-testid={`gegevens-veld-${naam}`}
        invalid={Boolean(fout)}
      />
      {fout ? (
        <p data-testid={`gegevens-fout-${naam}`} style={{ fontSize: "var(--fs-sm)", color: "var(--text-alarm)" }}>{fout}</p>
      ) : null}
    </div>
  );
}
